import { useState, useEffect } from 'react';
import HighlightCard from '../components/sections/HighlightCard';

export default function AllCampaignsPage() {
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const brandColor = '#e46809';

  useEffect(() => {
    const fetchAllCampaigns = async () => {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        setError("API URL is not configured.");
        setLoading(false);
        return;
      }

      try {
        // Fetch all campaigns
        const [recipientResponse, orgResponse] = await Promise.all([
          fetch(`${apiUrl}/campaigns`),
          fetch(`${apiUrl}/org-campaigns/active`)
        ]);
        
        // Check if responses are HTML (error page)
        const recipientContentType = recipientResponse.headers.get('content-type');
        const orgContentType = orgResponse.headers.get('content-type');
        
        if ((recipientContentType && recipientContentType.indexOf('text/html') !== -1) ||
            (orgContentType && orgContentType.indexOf('text/html') !== -1)) {
          throw new Error('Server returned HTML instead of JSON. Check API endpoint.');
        }
        
        if (!recipientResponse.ok || !orgResponse.ok) {
          throw new Error(`HTTP error! status: ${recipientResponse.status}, ${orgResponse.status}`);
        }
        
        const recipientData = await recipientResponse.json();
        const orgData = await orgResponse.json();
        
       // In the transformation of organization campaigns, add a fallback for organization_name
const transformedOrgCampaigns = orgData.map(campaign => ({
  id: `org-${campaign.id}`,
  type: 'organization',
  product_name: campaign.product_name,
  product_image_url: campaign.image_url,
  product_price: campaign.unit_price,
  // Add fallback for organization_name
  organization_name: campaign.organization_name || `${campaign.first_name} ${campaign.last_name}`,
  first_name: campaign.first_name,
  last_name: campaign.last_name,
  reason: campaign.reason,
  created_at: campaign.created_at,
  amount_raised: campaign.amount_raised,
  units_fulfilled: campaign.units_fulfilled,
  units_remaining: campaign.units_remaining,
  total_price: campaign.total_price,
  quantity: campaign.quantity
}));
        
        // Combine both types of campaigns
        const combinedCampaigns = [...recipientData, ...transformedOrgCampaigns];
        
        // Sort by creation date (newest first)
        combinedCampaigns.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        
        setAllCampaigns(combinedCampaigns);
      } catch (e) {
        console.error("Failed to fetch campaigns:", e);
        setError("Could not load campaigns. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchAllCampaigns();
  }, []);

  const renderContent = () => {
    if (loading) {
      return <p className="text-xl text-gray-500">Loading campaigns...</p>;
    }

    if (error) {
      return <p className="text-xl text-red-500">{error}</p>;
    }

    if (allCampaigns.length === 0) {
      return <p className="text-xl text-gray-500">No campaigns found.</p>;
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {allCampaigns.map((campaign) => (
          <HighlightCard key={campaign.id} highlight={campaign} />
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-900 py-16">
      <div className="container mx-auto px-4 text-center">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-4xl font-black uppercase text-gray-800 dark:text-white tracking-widest">
            All Campaigns
          </h1>
          <div className="w-24 h-1 mx-auto mt-4 mb-12" style={{ backgroundColor: brandColor }} />
          
          <div className="flex items-center justify-center">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
}